import { escapeHtml, formatMenuPriceParts } from '../format.js';
import { groupBySection, type MenuBlock, type MenuPage } from '../paginate.js';
import type { MenuBoardContent, MenuItemContent, MenuSection, MenuTheme } from '../types.js';
import {
  ARABIC_LINE,
  badge,
  description,
  fitScale,
  hasTitle,
  image,
  lineCount,
  price,
  sectionSubtitle,
  sellingPrice,
  variantLabel,
  type MenuFamily,
  type MenuStyleVariant,
  type MenuTemplate,
  type RenderContext,
} from './common.js';

/**
 * Ink Wall, layout A: tall black panels side by side, one per section, made for a row of
 * portrait screens or one wide video wall. Each panel has a huge white brush-script heading
 * (joining words like "and" in red), a big dish photo melting into the black, and its dishes
 * with thin leaders to the prices, or as a size table (SINGLE / DOUBLE) when they come in
 * sizes; a row of topping chips when the dishes list ingredients. The starred dish becomes a
 * red "make it a combo" disc at the foot of the first panel.
 *
 * In landscape a page holds four panels of exactly a quarter of the width each, so a 4×1 wall
 * of portrait screens (or four screens each showing a quarter) splits between panels; on one
 * portrait screen a page is one panel.
 *
 * The look (script headings, faded photos, lists, size tables, chips, combo disc) is shared
 * with ink-wall-hero and ink-wall-sizes.
 */

/* ------------------------------------------------------------------ shared look */

const svg = (markup: string) => `url("data:image/svg+xml,${encodeURIComponent(markup)}")`;

/** Fine specks, like salt and flour caught in a light beam. */
const SPECKS = svg(
  "<svg xmlns='http://www.w3.org/2000/svg' width='420' height='420'>" +
    "<filter id='s' x='0' y='0' width='100%' height='100%'><feTurbulence type='fractalNoise' baseFrequency='.9' numOctaves='1' seed='4' stitchTiles='stitch'/>" +
    "<feColorMatrix values='0 0 0 0 1 0 0 0 0 1 0 0 0 0 1 0 0 0 9 -7.6'/></filter>" +
    "<rect width='420' height='420' filter='url(#s)'/></svg>",
);

export const IW_FAMILY: Omit<MenuFamily, 'layout'> = { id: 'ink-wall', name: 'Ink Wall' };

export const IW_THEME: MenuTheme = {
  backgroundColor: '#0b0b0c',
  surfaceColor: '#1a1a1c',
  textColor: '#ffffff',
  primaryColor: '#ffffff',
  accentColor: '#e3241b',
  fontId: 'cafe',
  backgroundMediaId: null,
};

export const IW_VARIANTS: MenuStyleVariant[] = [
  { id: 'red', name: 'Black & red', theme: {} },
  { id: 'gold', name: 'Black & gold', theme: { accentColor: '#f0b429' } },
  { id: 'charcoal', name: 'Charcoal & orange', theme: { backgroundColor: '#1b1b1d', surfaceColor: '#29292c', accentColor: '#ff6a1a' } },
  { id: 'navy', name: 'Midnight & red', theme: { backgroundColor: '#0d1220', surfaceColor: '#182036', accentColor: '#ef3b36' } },
];

/** Black with a soft light in the middle and a few specks. */
export function iwRootCss(S: string): string {
  return `
${S}{background-color:var(--mb-bg);background-image:${SPECKS},radial-gradient(ellipse at 50% 40%,rgba(255,255,255,.07),transparent 65%);
  background-size:26em 26em,100% 100%}
${S}.mb-has-bg{background-image:none}`;
}

/** Words drawn in the accent colour inside script headings. */
const JOINERS = /^(and|&|with|or|n|'n'|’n’|of|the|y|et)$/i;

/** A heading in brush script, joining words in red; Arabic headings in the Arabic script face. */
export function iwScript(ctx: RenderContext, en: string | null | undefined, ar: string | null | undefined, cls: string): string {
  const enText = en?.trim() || '';
  const arText = ar?.trim() || '';
  const parts: string[] = [];
  const enLine = (text: string) =>
    `<p class="${cls}">${text
      .split(/\s+/)
      .map((w) => (JOINERS.test(w) ? `<span class="iw-joiner">${escapeHtml(w)}</span>` : escapeHtml(w)))
      .join(' ')}</p>`;
  if (ctx.showEn && enText) parts.push(enLine(enText));
  if (ctx.showAr && arText) parts.push(`<p class="${cls} mb-ar${parts.length ? ' mb-2nd' : ''}" dir="rtl" lang="ar">${escapeHtml(arText)}</p>`);
  if (!parts.length && enText) parts.push(enLine(enText));
  return parts.join('');
}

/** Lines a script heading wraps to when `perLine` characters fit on a line. */
export function iwLines(text: string | null | undefined, perLine: number): number {
  const words = (text ?? '').trim().split(/\s+/).filter(Boolean);
  if (!words.length) return 0;
  let lines = 1;
  let used = 0;
  for (const w of words) {
    if (used && used + 1 + w.length > perLine) {
      lines += 1;
      used = w.length;
    } else used += (used ? 1 : 0) + w.length;
  }
  return lines;
}

/** Height of an iwScript heading in em, at `em` size with `perLine` characters a line. */
export function iwScriptHeight(
  board: MenuBoardContent,
  en: string | null | undefined,
  ar: string | null | undefined,
  em: number,
  perLine: number,
  maxLines = 3,
): number {
  const lang = board.settings.language;
  const enText = en?.trim();
  const arText = ar?.trim();
  const longest = (enText ?? '').split(/\s+/).reduce((a, w) => (w.length > a.length ? w : a), '');
  const scale = fitScale(longest, perLine, 0.5);
  const enLines = lang !== 'ar' || !arText ? Math.min(maxLines, iwLines(enText, perLine / scale)) : 0;
  const arPart = lang !== 'en' && arText ? (enLines ? 0.55 : 0.9) * em * 1.35 * ARABIC_LINE : 0;
  return (enLines * em * 1.02 + arPart) * scale;
}

/** Wraps a script heading so its longest word fits its box. */
export function iwHeading(ctx: RenderContext, en: string | null | undefined, ar: string | null | undefined, cls: string, perLine: number): string {
  const longest = (ctx.showEn ? en?.trim() ?? '' : '').split(/\s+/).reduce((a, w) => (w.length > a.length ? w : a), '');
  const scale = fitScale(longest, perLine, 0.5);
  return `<div class="${cls}s" style="font-size:${scale.toFixed(3)}em">${iwScript(ctx, en, ar, cls)}</div>`;
}

export function iwScriptCss(S: string, cls: string, em: number): string {
  return `
${S} .${cls}{font-family:var(--mb-font-script);font-size:${em}em;line-height:1.02;color:var(--mb-primary);overflow-wrap:break-word;
  display:-webkit-box;-webkit-line-clamp:3;-webkit-box-orient:vertical;overflow:hidden;padding:.06em .1em .1em;text-shadow:0 .04em .12em rgba(0,0,0,.5)}
${S} .${cls}.mb-ar{line-height:1.35;-webkit-line-clamp:2;font-size:${(em * 0.9).toFixed(2)}em}
${S} .${cls}.mb-2nd{font-size:${(em * 0.55).toFixed(2)}em;color:var(--mb-accent);-webkit-line-clamp:1}
${S} .iw-joiner{color:var(--mb-accent)}`;
}

/** The dish photo: cover-cropped, its edges melting into the black. */
export function iwPhoto(ctx: RenderContext, mediaId: string | null | undefined, cls: string): string {
  const img = image(ctx, mediaId, 'iw-img');
  return img ? `<div class="iw-photo ${cls}">${img}</div>` : '';
}

export function iwPhotoCss(S: string): string {
  return `
${S} .iw-photo{position:relative;min-height:0;overflow:hidden}
${S} .iw-img{position:absolute;inset:0;width:100%;height:100%;object-fit:cover;
  -webkit-mask-image:radial-gradient(ellipse 50% 50% at 50% 50%,#000 45%,transparent 100%);mask-image:radial-gradient(ellipse 50% 50% at 50% 50%,#000 45%,transparent 100%)}`;
}

/* ------------------------------------------------------------------ lists and tables */

function names(ctx: RenderContext, item: MenuItemContent): { name: string; second: string } {
  const arabicFirst = !ctx.showEn && item.titleAr?.trim();
  const name = arabicFirst
    ? `<span class="iw-name mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr!.trim())}</span>`
    : `<span class="iw-name">${escapeHtml(item.title)}</span>`;
  const second =
    ctx.showEn && ctx.showAr && item.titleAr?.trim()
      ? `<p class="iw-name2 mb-ar" dir="rtl" lang="ar">${escapeHtml(item.titleAr.trim())}</p>`
      : '';
  return { name, second };
}

/** A dish with a thin leader running to its price (or its sizes), description under it. */
export function iwRow(ctx: RenderContext, item: MenuItemContent): string {
  const { name, second } = names(ctx, item);
  const badges = badge(ctx, item, 'iw-badge');
  const prices = item.variants.length
    ? `<span class="iw-sizes">${item.variants
        .map((v) => `<span class="iw-size"><small>${variantLabel(ctx, v.label, v.labelAr)}</small> <span dir="ltr">${price(ctx, v.price)}</span></span>`)
        .join('')}</span>`
    : `<span class="iw-pricebox">${sellingPrice(ctx, item, 'iw-price')}</span>`;
  return [
    '<div class="iw-row">',
    `<div class="iw-line">${name}${badges ? `<span class="iw-badges">${badges}</span>` : ''}<i class="iw-leader"></i>${prices}</div>`,
    second,
    description(ctx, item, 'iw-desc'),
    '</div>',
  ].join('');
}

/** Size labels for a table: every label the dishes use, in order of first use, three at most. */
export function iwSizeLabels(ctx: RenderContext, items: MenuItemContent[]): string[] {
  const labels: string[] = [];
  for (const item of items) {
    for (const v of item.variants) {
      const label = variantLabel(ctx, v.label, v.labelAr);
      if (!labels.includes(label) && labels.length < 3) labels.push(label);
    }
  }
  return labels;
}

/** A price without its currency, for table cells under a header that names it. */
function amount(ctx: RenderContext, value: number): string {
  const p = formatMenuPriceParts(value, ctx.board.settings.currency);
  return escapeHtml(p.fraction ? `${p.whole}.${p.fraction}` : p.whole);
}

/** Whether a block is drawn as a size table rather than a list. */
export function iwIsTable(items: MenuItemContent[]): boolean {
  return items.some((i) => i.variants.length > 0);
}

/**
 * Dishes in a table: the currency over the names and each size's label over its prices, a red
 * rule under the header. A dish without sizes shows its price in the first column.
 */
export function iwTable(ctx: RenderContext, b: MenuBlock): string {
  const labels = iwSizeLabels(ctx, b.items);
  const currency = formatMenuPriceParts(0, ctx.board.settings.currency).symbol;
  const cells = b.items.map((item) => {
    const { name, second } = names(ctx, item);
    const badges = badge(ctx, item, 'iw-badge');
    const prices = labels.map((label, i) => {
      const v = item.variants.find((x) => variantLabel(ctx, x.label, x.labelAr) === label);
      if (v) return `<span class="iw-tp" dir="ltr">${amount(ctx, v.price)}</span>`;
      if (!item.variants.length && i === 0) return `<span class="iw-tp" dir="ltr">${amount(ctx, item.discountPrice ?? item.price)}</span>`;
      return '<span class="iw-tp iw-tp-none">–</span>';
    });
    return `<div class="iw-tn"><div class="iw-line">${name}${badges ? `<span class="iw-badges">${badges}</span>` : ''}</div>${second}</div>${prices.join('')}`;
  });
  return [
    `<div class="iw-table" style="grid-template-columns:minmax(0,1fr) repeat(${labels.length},auto)">`,
    `<span class="iw-th iw-th-name"><small class="iw-cur">${escapeHtml(currency)}</small></span>`,
    labels.map((l) => `<span class="iw-th" dir="auto">${l}</span>`).join(''),
    '<i class="iw-trule"></i>',
    cells.join(''),
    '</div>',
  ].join('');
}

/** The ingredients the block's dishes list, as small chips with a coloured dot. */
export function iwToppings(ctx: RenderContext, items: MenuItemContent[]): string {
  const list = iwToppingList(ctx.board, items);
  if (!list.length) return '';
  return `<div class="iw-tops">${list
    .map((t, i) => `<span class="iw-top"><i style="--dot:${DOTS[i % DOTS.length]}"></i><span dir="auto">${escapeHtml(t)}</span></span>`)
    .join('')}</div>`;
}

const DOTS = ['#f6c544', '#e3402f', '#6bbf4e', '#f1e3c6', '#f28c28', '#9b6a3c'];

function iwToppingList(board: MenuBoardContent, items: MenuItemContent[]): string[] {
  if (!board.settings.showIngredients) return [];
  const arFirst = board.settings.language === 'ar';
  const seen: string[] = [];
  for (const item of items) {
    const list = arFirst && item.ingredientsAr.length ? item.ingredientsAr : item.ingredients;
    for (const raw of list) {
      const t = raw.trim();
      if (t && !seen.some((s) => s.toLowerCase() === t.toLowerCase()) && seen.length < 9) seen.push(t);
    }
  }
  return seen;
}

/** Height of the topping chips in em when `width` em are available. */
export function iwToppingsHeight(board: MenuBoardContent, items: MenuItemContent[], width: number): number {
  const list = iwToppingList(board, items);
  if (!list.length) return 0;
  let rows = 1;
  let used = 0;
  for (const t of list) {
    const w = (t.length * 0.5 + 2.2) * 0.85;
    if (used && used + w > width) {
      rows += 1;
      used = w;
    } else used += w;
  }
  return rows * 0.85 * 2.1 + 1;
}

/** Rows or a table, then the chips: the dishes of one block. */
export function iwDishes(ctx: RenderContext, b: MenuBlock): string {
  const body = iwIsTable(b.items) ? iwTable(ctx, b) : `<div class="iw-rows">${b.items.map((i) => iwRow(ctx, i)).join('')}</div>`;
  return body + iwToppings(ctx, b.items);
}

/** Height of iwDishes in em. */
export function iwDishesHeight(board: MenuBoardContent, items: MenuItemContent[], perLine: number, width: number): number {
  const en = board.settings.language !== 'ar';
  const ar = board.settings.language !== 'en';
  const table = iwIsTable(items);
  const rows = items.reduce((sum, item) => {
    const second = en && ar && item.titleAr?.trim() ? 1.1 * 1.3 * ARABIC_LINE : 0;
    const lines =
      !table && board.settings.showDescriptions
        ? (en || !item.descriptionAr ? lineCount(item.description, perLine, 2) : 0) +
          (ar ? lineCount(item.descriptionAr, perLine, 2) * ARABIC_LINE : 0)
        : 0;
    // Table names may wrap to a second line beside the price columns.
    const name = 1.4 * 1.3 * (table && item.title.length > 14 ? 2 : 1);
    return sum + name + second + (lines ? 0.2 + lines * 0.95 * 1.3 : 0) + (table ? 0.8 : 1);
  }, 0);
  return (table ? 1.3 * 1.3 + 1.2 : 0) + rows + iwToppingsHeight(board, items, width);
}

export function iwDishesCss(S: string): string {
  return `
${S} .iw-row{margin-bottom:1em}
${S} .iw-line{display:flex;align-items:baseline;gap:.6em;min-width:0}
${S} .iw-name{flex:0 1 auto;min-width:0;font-weight:700;font-size:1.4em;line-height:1.3;color:var(--mb-primary);white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .iw-name2{font-weight:700;font-size:1.1em;line-height:1.3;opacity:.75;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .iw-leader{flex:1 0 1.5em;align-self:center;height:.09em;background:var(--mb-text);opacity:.75;transform:translateY(.35em)}
${S} .iw-pricebox{display:flex;align-items:baseline;gap:.4em;white-space:nowrap}
${S} .iw-price{font-weight:700;font-size:1.4em;line-height:1.3;color:var(--mb-primary);white-space:nowrap}
${S} .iw-price-old{font-size:1em;opacity:.5;white-space:nowrap}
${S} .iw-sizes{display:flex;gap:.9em;white-space:nowrap}
${S} .iw-size{font-weight:700;font-size:1.3em;line-height:1.3}
${S} .iw-size small{font-size:.65em;font-weight:600;opacity:.7;text-transform:uppercase;letter-spacing:.04em}
${S} .iw-badges{display:flex;gap:.3em;flex-shrink:0;align-self:center}
${S} .iw-badge{font-size:.75em;font-weight:800;padding:.15em .55em;border-radius:99em;background:var(--mb-accent);color:#fff;white-space:nowrap}
${S} .iw-desc{font-size:.95em;line-height:1.3;opacity:.65;margin-top:.2em;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .iw-table{display:grid;column-gap:1.4em;align-items:baseline}
${S} .iw-th{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.3em;line-height:1.3;text-transform:uppercase;letter-spacing:.05em;
  text-align:center;white-space:nowrap}
${S} .iw-th-name{text-align:start;font-size:1.55em;line-height:1.1;overflow:hidden;text-overflow:ellipsis}
${S} .iw-th.mb-ar,${S} .iw-th[dir=auto]{letter-spacing:0}
${S} .iw-trule{grid-column:1/-1;height:.22em;margin:.5em 0 1em;background:linear-gradient(90deg,var(--mb-accent),var(--mb-accent) 70%,transparent);border-radius:.1em}
${S}[dir=rtl] .iw-trule{background:linear-gradient(270deg,var(--mb-accent),var(--mb-accent) 70%,transparent)}
${S} .iw-tn{min-width:0;margin-bottom:.8em}
${S} .iw-tn .iw-name{white-space:normal;display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;line-height:1.25}
${S} .iw-cur{font-size:.75em;letter-spacing:.08em;opacity:.7}
${S} .iw-tp{font-weight:700;font-size:1.4em;line-height:1.3;text-align:center;white-space:nowrap;margin-bottom:.8em}
${S} .iw-tp-none{opacity:.35}
${S} .iw-tops{display:flex;flex-wrap:wrap;gap:.5em 1.1em;font-size:.85em;margin-top:.4em;opacity:.95}
${S} .iw-top{display:inline-flex;align-items:center;gap:.45em;white-space:nowrap;line-height:1.6}
${S} .iw-top i{flex:0 0 auto;width:1.4em;height:1.4em;border-radius:50%;background:radial-gradient(circle at 35% 30%,rgba(255,255,255,.7),transparent 35%),var(--dot);
  box-shadow:0 .1em .25em rgba(0,0,0,.6)}`;
}

/* ------------------------------------------------------------------ combo disc */

/** The starred dish as a red disc: its badge on a pill, its name, its price. */
export function iwCombo(ctx: RenderContext, item: MenuItemContent, cls = 'iw-combo'): string {
  const label = ctx.showEn ? item.badge || item.badgeAr : item.badgeAr || item.badge;
  const title = !ctx.showEn && item.titleAr?.trim() ? item.titleAr.trim() : item.title;
  const amount = item.variants.length ? Math.min(...item.variants.map((v) => v.price)) : (item.discountPrice ?? item.price);
  const p = formatMenuPriceParts(amount, ctx.board.settings.currency);
  const number = p.fraction ? `${p.whole}.${p.fraction}` : p.whole;
  const priceText = p.before ? `${p.symbol}${number}` : `${number} ${p.symbol}`;
  return [
    `<div class="${cls}"><i class="iw-rays"></i><div class="iw-disc">`,
    label?.trim() ? `<span class="iw-pill" dir="auto">${escapeHtml(label.trim())}</span>` : '',
    `<p class="iw-ctitle" dir="auto" style="font-size:${(2.1 * fitScale(title, 11, 0.55)).toFixed(3)}em">${escapeHtml(title)}</p>`,
    `<p class="iw-cprice" dir="ltr" style="font-size:${(2 * fitScale(priceText, 7, 0.6)).toFixed(3)}em">${escapeHtml(priceText)}</p>`,
    '</div></div>',
  ].join('');
}

export const IW_COMBO_HEIGHT = 15;

export function iwComboCss(S: string, cls = 'iw-combo'): string {
  return `
${S} .${cls}{position:relative;width:13em;height:13em;flex:0 0 auto}
${S} .iw-disc{position:absolute;inset:0;border-radius:50%;background:var(--mb-accent);color:#fff;display:flex;flex-direction:column;align-items:center;justify-content:center;
  text-align:center;padding:1.4em;box-shadow:0 0 0 .5em var(--mb-bg),0 0 0 .8em var(--mb-accent),0 1em 2em rgba(0,0,0,.6)}
${S} .iw-pill{max-width:100%;font-weight:800;font-size:.95em;text-transform:uppercase;letter-spacing:.04em;background:#fff;color:var(--mb-accent);
  padding:.2em .8em;border-radius:99em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis;transform:rotate(-4deg)}
${S} .iw-ctitle{max-width:100%;font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);line-height:1.1;text-transform:uppercase;margin-top:.15em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
${S} .iw-cprice{font-weight:800;line-height:1.2;white-space:nowrap}
${S} .iw-rays{position:absolute;z-index:1;top:-.6em;left:.2em;width:3.2em;height:3.2em;
  background:linear-gradient(var(--mb-accent),var(--mb-accent)) 0 60%/1.3em .32em no-repeat,linear-gradient(var(--mb-accent),var(--mb-accent)) 40% 0/.32em 1.3em no-repeat;
  transform:rotate(-20deg)}
${S} .iw-rays::after{content:'';position:absolute;left:.15em;top:.3em;width:1.2em;height:.32em;background:var(--mb-accent);transform:rotate(45deg);transform-origin:left}`;
}

/** The starred dish, only when one is starred: a combo is a choice, not a fallback. */
export function iwStarred(board: MenuBoardContent): MenuItemContent | null {
  return (board.featuredItemId && board.items.find((i) => i.id === board.featuredItemId)) || null;
}

/* ------------------------------------------------------------------ layout A */

const S = '.mb-t-ink-wall-panels';
/** One portrait screen draws its single panel larger. */
const PORTRAIT_SCALE = 1.6;
const PANELS = { landscape: 4, portrait: 1 };
const TITLE_EM = 5.6;
const TITLE_CHARS = 8.5;
/** Smallest height a panel's photo shrinks to. */
const MIN_PHOTO = { landscape: 11, portrait: 12 };

/** Panel size in its own em: height and inner width. */
function panelBox(portrait: boolean): { height: number; width: number } {
  return portrait ? { height: 120 / PORTRAIT_SCALE, width: 67.5 / PORTRAIT_SCALE - 5 } : { height: 67.5, width: 120 / 4 - 4.4 };
}

function panelPhoto(b: MenuBlock): string | null {
  return (!b.continued && b.section.imageMediaId) || b.items.find((i) => i.imageMediaId)?.imageMediaId || b.section.imageMediaId || null;
}

function panelCost(board: MenuBoardContent, section: MenuSection, items: MenuItemContent[], portrait: boolean, combo: boolean): number {
  const box = panelBox(portrait);
  const title = hasTitle(section) ? iwScriptHeight(board, section.title, section.titleAr, TITLE_EM, TITLE_CHARS * (portrait ? 1.4 : 1)) + 0.6 : 0;
  const sub = section.subtitle?.trim() || section.subtitleAr?.trim() ? 1.9 : 0;
  const photo = items.some((i) => i.imageMediaId) || section.imageMediaId ? MIN_PHOTO[portrait ? 'portrait' : 'landscape'] + 1.4 : 0;
  return 2.6 + title + sub + photo + iwDishesHeight(board, items, portrait ? 60 : 44, box.width) + (combo ? IW_COMBO_HEIGHT : 0) + 2.4;
}

function paginate(board: MenuBoardContent, portrait: boolean): MenuPage[] {
  const starred = iwStarred(board);
  const box = panelBox(portrait);
  const panels: MenuBlock[] = [];
  for (const group of groupBySection(board, starred?.id)) {
    let block: MenuBlock = { section: group.section, items: [], continued: false };
    for (const item of group.items) {
      const combo = Boolean(starred) && panels.length === 0;
      const fits = panelCost(board, group.section, [...block.items, item], portrait, combo) <= box.height;
      if (!fits && block.items.length) {
        panels.push(block);
        block = { section: group.section, items: [], continued: true };
      }
      block.items.push(item);
    }
    if (block.items.length) panels.push(block);
  }
  const per = PANELS[portrait ? 'portrait' : 'landscape'];
  const pages: MenuPage[] = [];
  for (let i = 0; i < panels.length; i += per) pages.push({ columns: panels.slice(i, i + per).map((b) => [b]) });
  return pages.length ? pages : [{ columns: [[]] }];
}

function panel(ctx: RenderContext, b: MenuBlock | undefined, combo: MenuItemContent | null, index: number): string {
  const { board } = ctx;
  const title = b
    ? hasTitle(b.section)
      ? iwHeading(ctx, b.section.title, b.section.titleAr, 'iw1-title', TITLE_CHARS * (ctx.orientation === 'portrait' ? 1.4 : 1))
      : ''
    : iwHeading(ctx, board.settings.title ?? board.name, board.settings.titleAr, 'iw1-title', TITLE_CHARS);
  const sub = b && !b.continued ? sectionSubtitle(ctx, b.section, 'iw1-sub') : '';
  return [
    `<div class="iw1-panel iw1-panel-${index % 2 ? 'b' : 'a'}">`,
    `<div class="iw1-top">${title}${sub}</div>`,
    b ? iwPhoto(ctx, panelPhoto(b), 'iw1-photo') : '',
    `<div class="iw1-dishes">${b ? iwDishes(ctx, b) : ''}</div>`,
    combo ? `<div class="iw1-foot">${iwCombo(ctx, combo)}</div>` : '',
    '</div>',
  ].join('');
}

export const inkWallPanels: MenuTemplate = {
  id: 'ink-wall-panels',
  name: 'Ink Wall · Four panels',
  description:
    'Tall black panels side by side, one per section, made for a row of portrait screens or one wide video wall: huge brush-script headings with red joining words, a big photo melting into the black, prices on thin leaders or in a size table, topping chips and a red combo disc for the starred dish. Four panels split evenly across four screens.',
  defaultTheme: IW_THEME,
  variants: IW_VARIANTS,
  cuisines: ['burgers', 'drinks'],
  family: { ...IW_FAMILY, layout: 'Four panels' },
  uses: { featured: true, sectionImages: true, brand: false, decorations: [] },

  paginate(board, orientation) {
    return paginate(board, orientation === 'portrait');
  },

  render(ctx) {
    const starred = ctx.pageIndex === 0 ? iwStarred(ctx.board) : null;
    const cols = ctx.page.columns.length ? ctx.page.columns : [[]];
    const panels = cols.map((c, i) => panel(ctx, c[0], i === 0 ? starred : null, i)).join('');
    return `<div class="iw1-panels">${panels}</div>`;
  },

  css: `
${iwRootCss(S)}
${iwScriptCss(S, 'iw1-title', TITLE_EM)}
${iwPhotoCss(S)}
${iwDishesCss(S)}
${iwComboCss(S)}
${S} .iw1-panels{flex:1;min-height:0;display:flex}
${S} .iw1-panel{position:relative;flex:1 1 0;min-width:0;min-height:0;display:flex;flex-direction:column;padding:2.6em 2.2em 2.4em}
${S}.mb-portrait .iw1-panel{font-size:${PORTRAIT_SCALE}em;padding:2.6em 2.5em 2.4em}
${S} .iw1-panel-b{background:linear-gradient(rgba(255,255,255,.035),rgba(255,255,255,.02))}
${S} .iw1-top{flex:0 0 auto;margin-bottom:.6em}
${S} .iw1-titles{min-width:0}
${S} .iw1-sub{font-family:var(--mb-font-heading);font-weight:var(--mb-heading-weight);font-size:1.2em;line-height:1.4;letter-spacing:.16em;text-transform:uppercase;
  color:var(--mb-accent);margin-top:.2em;white-space:nowrap;overflow:hidden;text-overflow:ellipsis}
${S} .iw1-sub.mb-ar{letter-spacing:0}
${S} .iw1-photo{flex:1 1 ${MIN_PHOTO.landscape}em;min-height:${MIN_PHOTO.landscape}em;max-height:34em;margin:0 -2.2em 1.4em}
${S}.mb-portrait .iw1-photo{flex-basis:${MIN_PHOTO.portrait}em;min-height:${MIN_PHOTO.portrait}em;max-height:40em;margin:0 -2.5em 1.4em}
${S} .iw1-dishes{flex:0 0 auto;margin-top:auto}
${S} .iw1-foot{flex:0 0 auto;display:flex;justify-content:flex-end;padding-top:1em}
`,
};
